import {
  FC,
  memo,
  useCallback,
  useState,
} from 'react';
import { App, Divider } from 'antd';
import { useAppDispatch } from 'app/providers/Store';
import { createFormalizedZip } from 'entities/formalizedZip';
import { Form } from 'features/Form';
import { FormalizedZipRequestDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';
import { Modal } from 'shared/ui/Modal';
import { AttachmentDownloadList } from 'shared/ui';
import { FORMALIZED_ZIP_FORM_FIELDS } from '../../../FormalizedZip/constants/formalizedZipConstants';

interface FormalizeModalProps {
  isOpen: boolean;
  /** Все вложения ЗИП: админ сам выбирает, какое скачать. */
  attachmentUuids?: string[];
  unformalizedZipId: number;
  engineId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export const FormalizeModal: FC<FormalizeModalProps> = memo(({
  isOpen,
  attachmentUuids,
  unformalizedZipId,
  engineId,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { showSpinner, hideSpinner } = useGlobalSpinner();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (data: FormalizedZipRequestDto) => {
    try {
      setLoading(true);
      showSpinner('Создание формализованного ЗИП...');

      await dispatch(createFormalizedZip({
        ...data,
        unformalizedZipId,
        engineId,
      })).unwrap();

      message.success('Формализованный ЗИП успешно создан');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Ошибка при создании формализованного ЗИП:', error);
      message.error('Ошибка при создании формализованного ЗИП');
    } finally {
      setLoading(false);
      hideSpinner();
    }
  }, [dispatch, engineId, onSuccess, onClose, showSpinner, hideSpinner, message]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Начальные значения с предзаполненным engineId
  const initialValues = {
    engineId,
    zipOrderCode: '',
    installationLocation: '',
    description: '',
    count: 1,
  };

  return (
    <Modal
      title="Формализовать ЗИП"
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={600}
    >
      <AttachmentDownloadList
        attachmentUuids={attachmentUuids}
        label="Неформализованные файлы по ЗИП"
        emptyText="Файлы не приложены"
        disabled={loading}
      />
      <Divider />

      <Form
        fields={FORMALIZED_ZIP_FORM_FIELDS}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </Modal>
  );
});

FormalizeModal.displayName = 'FormalizeModal';
